// Import CSS for bundling
import "./datetimepicker.css";

/**
 * Date/time picker widget: renders a calendar grid + time spinners inside
 * a BasecoatUI-styled popover ([data-popover], same CSS component basecoat
 * ships for .popover/.dropdown-menu). Despite the shared CSS, this app
 * doesn't actually load basecoat's own bundled popover.js -- it only has
 * its own hand-rolled dropdown-menu.js for *menu*-shaped dropdowns
 * (requires a [role="menu"] child), which this widget's calendar/time
 * content isn't. So open/close/outside-click/Escape are implemented here
 * directly, mirroring basecoat's popover.js behavior (and its
 * `basecoat:popover` coordination event, already used by
 * dropdown-menu.js) rather than depending on it.
 */
class DateTimePickerWidget {
  constructor(container) {
    this.container = container;
    this.fieldKey = container.dataset.field;
    this.mode = container.dataset.mode || "date";
    this.minDate = this.parseDateOnly(container.dataset.minDate);
    this.maxDate = this.parseDateOnly(container.dataset.maxDate);
    // The earliest moment that can be picked (wall clock, minutes): on its
    // day, an earlier time moves up to it.
    this.minDateTime = this.parseMinDateTime(container.dataset.minDatetime);
    this.disabledDates = new Set(
      JSON.parse(container.dataset.disabledDates || "[]"),
    );
    this.hour24 = container.dataset.timeFormat !== "12";
    this.enableSeconds = container.dataset.enableSeconds === "true";
    this.firstDayOfWeek = parseInt(container.dataset.firstDayOfWeek, 10) || 0;
    this.locale = container.dataset.locale || undefined;
    this.i18n = JSON.parse(container.dataset.i18n || "{}");

    // When set, an existing element elsewhere on the page acts as the
    // trigger (and owns its own .popover-positioned wrapper) instead of
    // the button/display span this widget would otherwise render itself
    // -- see widget.py's `external_trigger_id` docstring.
    this.externalTriggerId = container.dataset.externalTrigger || null;

    this.hiddenInput = container.querySelector(`#${this.fieldKey}`);
    this.trigger = this.externalTriggerId
      ? document.getElementById(this.externalTriggerId)
      : container.querySelector(`#${this.fieldKey}-trigger`);
    this.display = container.querySelector(`#${this.fieldKey}-display`);
    this.panel = container.querySelector(`#${this.fieldKey}-panel`);
    this.monthSelect = container.querySelector('[data-role="month-select"]');
    this.yearSelect = container.querySelector('[data-role="year-select"]');
    this.weekdaysEl = container.querySelector('[data-role="weekdays"]');
    this.daysEl = container.querySelector('[data-role="days"]');
    this.hourInput = container.querySelector('[data-role="hour"]');
    this.minuteInput = container.querySelector('[data-role="minute"]');
    this.secondInput = container.querySelector('[data-role="second"]');
    this.ampmToggle = container.querySelector('[data-role="ampm-toggle"]');
    // The template renders the placeholder as the display span's initial
    // text -- captured once here so updateDisplay() can restore it
    // exactly (rather than guessing) whenever there's no selected value.
    // No display span at all in external-trigger mode (the caller owns
    // whatever label its own trigger shows), so updateDisplay() is a no-op.
    this.placeholderText = this.display ? this.display.textContent : null;

    // The selected value (Date object, local time, or null). `viewDate`
    // is always day=1 of whichever month the calendar is currently
    // showing -- decoupled from `selectedDate` so browsing other months
    // doesn't change the value until a day is actually clicked.
    this.selectedDate = this.parseStoredValue(this.hiddenInput.value);
    this.viewDate = this.selectedDate
      ? new Date(
          this.selectedDate.getFullYear(),
          this.selectedDate.getMonth(),
          1,
        )
      : new Date(new Date().getFullYear(), new Date().getMonth(), 1);

    this.init();
  }

  init() {
    if (this.daysEl) {
      this.renderMonthYearSelects();
      this.renderWeekdays();
      this.renderCalendar();
    }
    if (this.hourInput) this.syncTimeInputsFromState();
    this.updateDisplay();
    // bindEvents() queries for buttons/inputs inside the panel via
    // `this.container.querySelector(...)` -- it must run while the panel
    // is still a descendant of `this.container`, before portalPanel()
    // moves it to <body>. Reparenting afterwards doesn't detach listeners
    // already bound to the actual DOM nodes, so ordering it this way still
    // leaves the panel fully portalled and fully wired.
    this.bindEvents();
    this.portalPanel();
    // Without this, the panel sits at whatever position basecoat's
    // [data-popover] CSS defaults to (bottom-left corner) from page load
    // until the very first open -- positionPanel() only ever ran inside
    // openPopover() before, so that first open's inline top/left change
    // was a real style transition FROM that default corner, visibly
    // "traveling" across the screen to the trigger. Positioning once here,
    // before the panel is ever shown, means every open (including the
    // first) starts from the correct spot with nothing to animate from.
    this.positionPanel();
  }

  // Moves the panel to a direct child of <body>, positioned with
  // `position: fixed` (computed per-open in positionPanel()) instead of
  // relying on CSS `position: absolute` inside wherever this widget
  // happens to be rendered in the form. A deeply-nested form (tabs, cards,
  // scroll containers) can clip an absolutely-positioned descendant via an
  // ancestor's `overflow` or bury it under later-painted siblings that
  // don't happen to share its stacking context -- the exact "panel is
  // offscreen / clickable elements are hidden underneath something else"
  // failure mode this works around. Same reason flatpickr itself renders
  // into <body> by default. Moving the node preserves every listener
  // already bound to its descendants (querySelector results are live DOM
  // references, unaffected by reparenting) -- only the outside-click/
  // Escape handling below needed updating to account for the panel no
  // longer being a descendant of `this.container`.
  //
  // Inside a <dialog> the panel goes into the dialog instead: an open
  // modal dialog sits in the top layer and makes the rest of the page
  // inert, so a panel in <body> would open behind it, out of reach.
  portalPanel() {
    if (!this.panel) return;
    const host = this.container.closest("dialog") || document.body;
    if (this.panel.parentElement === host) return;
    host.appendChild(this.panel);
    this.panel.style.position = "fixed";
    this.panel.style.margin = "0";
    // Basecoat's own [data-popover] CSS sets `min-width: 100%`, meant to
    // match the trigger's width when positioned via position: absolute
    // inside `.popover`. Under position: fixed that percentage resolves
    // against the viewport instead, which would stretch the panel to the
    // full window width -- neutralized here since we size/position this
    // panel via JS instead.
    this.panel.style.minWidth = "0";
    // A dialog's own CSS may center its direct children (`translate:
    // -50% -50%`, as the app dialogs do): not this one.
    this.panel.style.translate = "none";
  }

  // ---- Value parsing/formatting -----------------------------------

  pad(n) {
    return String(n).padStart(2, "0");
  }

  parseMinDateTime(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || "");
    if (!match) return null;
    const [, y, m, d, h, min] = match.map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, h, min, 0, 0);
  }

  // Moves a selection earlier than `minDateTime` up to it (and shows the
  // corrected time). True when it changed anything.
  clampToMin() {
    if (!this.minDateTime || !this.selectedDate || this.mode !== "datetime") return false;
    if (this.selectedDate >= this.minDateTime) return false;
    this.selectedDate = new Date(this.minDateTime);
    if (this.hourInput) this.syncTimeInputsFromState();
    return true;
  }

  parseDateOnly(value) {
    if (!value) return null;
    const [y, m, d] = value.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  parseStoredValue(value) {
    if (!value) return null;
    // 'YYYY-MM-DD', 'HH:MM[:SS]', or 'YYYY-MM-DDTHH:MM[:SS]'
    const [datePart, timePart] = value.includes("T")
      ? value.split("T")
      : this.mode === "time"
        ? [null, value]
        : [value, null];

    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();
    let day = now.getDate();
    if (datePart) {
      const [y, m, d] = datePart.split("-").map((n) => parseInt(n, 10));
      if (!y || !m || !d) return null;
      year = y;
      month = m - 1;
      day = d;
    }

    let hour = 0;
    let minute = 0;
    let second = 0;
    if (timePart) {
      const parts = timePart.split(":").map((n) => parseInt(n, 10));
      hour = parts[0] || 0;
      minute = parts[1] || 0;
      second = parts[2] || 0;
    }

    return new Date(year, month, day, hour, minute, second);
  }

  formatStoredValue(date) {
    if (!date) return "";
    const datePart = `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
    let timePart = `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}`;
    if (this.enableSeconds) timePart += `:${this.pad(date.getSeconds())}`;

    if (this.mode === "date") return datePart;
    if (this.mode === "time") return timePart;
    return `${datePart}T${timePart}`;
  }

  dateKey(date) {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
  }

  isSameDay(a, b) {
    return (
      a &&
      b &&
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  isDisabled(date) {
    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;
    return this.disabledDates.has(this.dateKey(date));
  }

  // ---- Rendering -----------------------------------------------------

  renderMonthYearSelects() {
    const monthFormatter = new Intl.DateTimeFormat(this.locale, {
      month: "long",
    });
    this.monthSelect.innerHTML = "";
    for (let m = 0; m < 12; m++) {
      const option = document.createElement("option");
      option.value = String(m);
      option.textContent = monthFormatter.format(new Date(2000, m, 1));
      this.monthSelect.appendChild(option);
    }

    const currentYear = new Date().getFullYear();
    const minYear = this.minDate ? this.minDate.getFullYear() : currentYear - 100;
    const maxYear = this.maxDate ? this.maxDate.getFullYear() : currentYear + 100;
    this.yearSelect.innerHTML = "";
    for (let y = minYear; y <= maxYear; y++) {
      const option = document.createElement("option");
      option.value = String(y);
      option.textContent = String(y);
      this.yearSelect.appendChild(option);
    }

    this.monthSelect.value = String(this.viewDate.getMonth());
    this.yearSelect.value = String(this.viewDate.getFullYear());
  }

  // The header row is seven fixed 2rem columns, so the labels in it have
  // to be about four characters at most. `weekday: "short"` usually is --
  // "Mon", "lun.", "Mo" -- but CLDR has locales where it is not, and the
  // overflow is silent: seven long labels in 2rem columns render on top of
  // each other as an unreadable smear.
  //
  // pt-PT is the case that surfaced this. Its short form is the *full*
  // name ("domingo", "segunda"), while plain `pt` -- and pt-BR, which
  // inherits from it -- carries the real abbreviations "dom.", "seg.",
  // "ter.". The abbreviations are correct European Portuguese; the pt-PT
  // data simply lacks them. Retrying with the base language subtag is a
  // general fix for exactly that shape of gap, not a special case for one
  // locale, and it is tried before `narrow` because a proper abbreviation
  // beats a single letter wherever one exists.
  //
  // `narrow` is the last resort, for languages where nothing short enough
  // exists at all (Arabic, Thai, Vietnamese). It is ambiguous in some --
  // Portuguese narrow is D S T Q Q S S, with two pairs -- which is
  // precisely why it is not tried first.
  weekdayLabels() {
    const MAX_GRAPHEMES = 4;
    // 2023-01-01 was a Sunday, so day 0 of this week is index 0.
    const reference = new Date(2023, 0, 1);
    const format = (locale, style) => {
      const formatter = new Intl.DateTimeFormat(locale, { weekday: style });
      return Array.from({ length: 7 }, (unused, day) =>
        formatter.format(
          new Date(
            reference.getFullYear(),
            reference.getMonth(),
            reference.getDate() + day,
          ),
        ),
      );
    };
    // Array.from, not .length: a label can contain characters that take
    // two UTF-16 code units, and counting those as two would reject a
    // label that actually fits.
    const fits = (labels) =>
      labels.every((label) => Array.from(label).length <= MAX_GRAPHEMES);

    let labels;
    try {
      labels = format(this.locale, "short");
    } catch {
      // An unusable locale tag: let Intl pick the runtime default rather
      // than rendering no header at all.
      return format(undefined, "short");
    }
    if (fits(labels)) return labels;

    const base = String(this.locale || "").split("-")[0];
    if (base && base !== this.locale) {
      try {
        const baseLabels = format(base, "short");
        if (fits(baseLabels)) return baseLabels;
      } catch {
        // Fall through to narrow.
      }
    }
    return format(this.locale, "narrow");
  }

  renderWeekdays() {
    // Reordered to start at `firstDayOfWeek` (0=Sunday..6=Saturday).
    const labels = this.weekdayLabels();
    this.weekdaysEl.innerHTML = "";
    for (let i = 0; i < 7; i++) {
      const span = document.createElement("span");
      span.textContent = labels[(this.firstDayOfWeek + i) % 7];
      this.weekdaysEl.appendChild(span);
    }
  }

  renderCalendar() {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const startOffset = (firstOfMonth.getDay() - this.firstDayOfWeek + 7) % 7;
    const gridStart = new Date(year, month, 1 - startOffset);

    const today = new Date();
    this.daysEl.innerHTML = "";

    for (let i = 0; i < 42; i++) {
      const cellDate = new Date(
        gridStart.getFullYear(),
        gridStart.getMonth(),
        gridStart.getDate() + i,
      );
      const button = document.createElement("button");
      button.type = "button";
      button.className = "widget-datetimepicker__day";
      button.textContent = String(cellDate.getDate());
      button.dataset.date = this.dateKey(cellDate);
      button.setAttribute("role", "gridcell");

      if (cellDate.getMonth() !== month) {
        button.classList.add("widget-datetimepicker__day--outside");
      }
      if (this.isSameDay(cellDate, today)) {
        button.classList.add("widget-datetimepicker__day--today");
      }
      if (this.isSameDay(cellDate, this.selectedDate)) {
        button.classList.add("widget-datetimepicker__day--selected");
        button.setAttribute("aria-selected", "true");
      }
      if (this.isDisabled(cellDate)) {
        button.disabled = true;
        button.setAttribute("aria-disabled", "true");
      }

      // Roving tabindex: only one focusable cell at a time (the selected
      // day, or today, or the 1st of the month as a last resort).
      const isRovingTarget = this.selectedDate
        ? this.isSameDay(cellDate, this.selectedDate)
        : this.isSameDay(cellDate, today) && cellDate.getMonth() === month
          ? true
          : cellDate.getDate() === 1 && cellDate.getMonth() === month;
      button.tabIndex = isRovingTarget ? 0 : -1;

      this.daysEl.appendChild(button);
    }
  }

  updateDisplay() {
    if (!this.display) return;
    if (!this.selectedDate) {
      this.display.textContent = this.placeholderText;
      return;
    }
    const options =
      this.mode === "date"
        ? { year: "numeric", month: "long", day: "numeric" }
        : this.mode === "time"
          ? { hour: "2-digit", minute: "2-digit", hour12: !this.hour24 }
          : {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: !this.hour24,
            };
    this.display.textContent = new Intl.DateTimeFormat(this.locale, options).format(
      this.selectedDate,
    );
  }

  syncTimeInputsFromState() {
    const source = this.selectedDate || new Date(0, 0, 1, 0, 0, 0);
    let hour = source.getHours();
    if (!this.hour24) {
      const isPM = hour >= 12;
      hour = hour % 12 || 12;
      if (this.ampmToggle) {
        this.ampmToggle.dataset.value = isPM ? "PM" : "AM";
        this.ampmToggle.textContent = isPM ? "PM" : "AM";
      }
    }
    this.hourInput.value = this.pad(hour);
    this.minuteInput.value = this.pad(source.getMinutes());
    if (this.secondInput) this.secondInput.value = this.pad(source.getSeconds());
  }

  // ---- State mutation --------------------------------------------------

  ensureSelectedDate(fallbackDate) {
    if (!this.selectedDate) {
      this.selectedDate = fallbackDate ? new Date(fallbackDate) : new Date();
      this.selectedDate.setHours(0, 0, 0, 0);
    }
    return this.selectedDate;
  }

  selectDay(date) {
    if (this.isDisabled(date)) return;
    const hours = this.selectedDate ? this.selectedDate.getHours() : 0;
    const minutes = this.selectedDate ? this.selectedDate.getMinutes() : 0;
    const seconds = this.selectedDate ? this.selectedDate.getSeconds() : 0;
    this.selectedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      seconds,
    );
    this.viewDate = new Date(date.getFullYear(), date.getMonth(), 1);
    this.clampToMin();
    this.commit();
    this.renderCalendar();
    this.updateDisplay();

    if (this.mode === "date") {
      this.closePopover(true);
    }
  }

  applyTimeFromInputs() {
    this.ensureSelectedDate();
    let hour = parseInt(this.hourInput.value, 10);
    if (Number.isNaN(hour)) hour = 0;
    if (!this.hour24) {
      hour = hour % 12;
      if (this.ampmToggle && this.ampmToggle.dataset.value === "PM") hour += 12;
    }
    hour = Math.min(23, Math.max(0, hour));
    const minute = Math.min(59, Math.max(0, parseInt(this.minuteInput.value, 10) || 0));
    const second = this.secondInput
      ? Math.min(59, Math.max(0, parseInt(this.secondInput.value, 10) || 0))
      : 0;

    this.selectedDate.setHours(hour, minute, second, 0);
    this.clampToMin();
    this.commit();
    this.updateDisplay();
  }

  // Custom up/down steppers (native spinner buttons are hidden -- see
  // datetimepicker.css) -- hour wraps within whatever range is currently
  // displayed (1-12 or 0-23 depending on hour12/24 display), delegating
  // the 12h -> 24h conversion to applyTimeFromInputs() same as manual
  // typing does.
  bumpHour(delta) {
    let hour = parseInt(this.hourInput.value, 10);
    if (Number.isNaN(hour)) hour = this.hour24 ? 0 : 12;
    const min = this.hour24 ? 0 : 1;
    const max = this.hour24 ? 23 : 12;
    const span = max - min + 1;
    hour = min + (((hour - min + delta) % span) + span) % span;
    this.hourInput.value = this.pad(hour);
    this.applyTimeFromInputs();
  }

  bumpMinute(delta) {
    let minute = parseInt(this.minuteInput.value, 10);
    if (Number.isNaN(minute)) minute = 0;
    minute = ((minute + delta) % 60 + 60) % 60;
    this.minuteInput.value = this.pad(minute);
    this.applyTimeFromInputs();
  }

  commit() {
    this.hiddenInput.value = this.formatStoredValue(this.selectedDate);
    this.hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  clear() {
    this.selectedDate = null;
    this.hiddenInput.value = "";
    this.hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
    if (this.daysEl) this.renderCalendar();
    if (this.hourInput) this.syncTimeInputsFromState();
    this.updateDisplay();
  }

  goToToday() {
    const now = new Date();
    if (this.mode === "time") {
      this.selectedDate = this.ensureSelectedDate(now);
      this.selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
      this.commit();
      this.syncTimeInputsFromState();
      this.updateDisplay();
      return;
    }
    this.viewDate = new Date(now.getFullYear(), now.getMonth(), 1);
    this.renderMonthYearSelects();
    this.renderCalendar();
    if (!this.isDisabled(now)) {
      this.selectDay(now);
    }
  }

  // ---- Popover open/close -- mirrors basecoat's own popover.js (which
  // this app doesn't actually load, see the class-level comment above):
  // aria-expanded/aria-hidden toggle, outside click, Escape, and a
  // `basecoat:popover` event so opening this picker closes any other
  // open dropdown-menu/picker on the page, and vice versa. ------------

  isOpen() {
    return this.trigger.getAttribute("aria-expanded") === "true";
  }

  openPopover() {
    if (this.isOpen()) return;
    document.dispatchEvent(
      new CustomEvent("basecoat:popover", { detail: { source: this.container } }),
    );
    if (this.daysEl) this.renderCalendar();
    if (this.hourInput) this.syncTimeInputsFromState();
    this.positionPanel();
    this.trigger.setAttribute("aria-expanded", "true");
    this.panel.setAttribute("aria-hidden", "false");
  }

  // Positions the (portalled, position:fixed) panel against the trigger,
  // flipping above/below and clamping both axes to the viewport so it's
  // always fully visible -- recomputed on every open, since
  // scrolling/resizing between opens can change how much room is actually
  // available.
  positionPanel() {
    // No trigger/panel to position at all in inline mode (see the
    // `inline` widget option) -- everything just renders in normal flow.
    if (!this.trigger || !this.panel) return;
    const anchor = this.trigger.closest(".popover") || this.trigger;
    const anchorRect = anchor.getBoundingClientRect();
    // Where the anchor sat at placement time. The scroll handler compares
    // against this to tell a scroll that actually moved the trigger (the
    // panel is now drifting, so close it) from one that did not (nothing
    // to drift, leave it alone) -- see bindEvents.
    this.anchorRectAtOpen = { top: anchorRect.top, left: anchorRect.left };
    // data-side still drives the CSS open/close slide transition
    // (translate direction) -- only top/left are used for actual
    // placement now that the panel is fixed-positioned in <body>.
    // This runs while the panel is still aria-hidden="true", which
    // carries basecoat's own `scale: 95%` transform -- getBoundingClientRect
    // would report the *scaled-down* box, undershooting the real size the
    // panel snaps to once aria-hidden flips to "false" a few lines below,
    // letting it overshoot the clamp below. offsetWidth/offsetHeight are
    // untransformed layout-box dimensions, unaffected by that scale.
    const panelHeight = this.panel.offsetHeight || 380;
    const panelWidth = this.panel.offsetWidth || 280;
    const margin = 8;

    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const side =
      spaceBelow >= panelHeight + margin || spaceBelow >= spaceAbove
        ? "bottom"
        : "top";
    this.panel.setAttribute("data-side", side);

    let top = side === "bottom" ? anchorRect.bottom + margin : anchorRect.top - panelHeight - margin;
    top = Math.max(margin, Math.min(top, window.innerHeight - panelHeight - margin));

    let left = anchorRect.left;
    left = Math.max(margin, Math.min(left, window.innerWidth - panelWidth - margin));

    // Basecoat's own [data-popover] CSS sets physical properties (e.g.
    // `bottom: 100%` for data-side="top") *and* logical ones (e.g.
    // `inset-inline-start: 0` for data-align="start") depending on the
    // data-side/data-align combo. Both map to the same physical edges as
    // the top/left this JS sets below, and per the CSS Logical
    // Properties spec, when a physical and a logical property target the
    // same edge, whichever is *declared later* in the style wins --
    // regardless of which one this JS "meant" as authoritative. Clear all
    // four here, before top/left are set, so top/left (declared after)
    // are always the ones that stick.
    this.panel.style.bottom = "auto";
    this.panel.style.right = "auto";
    this.panel.style.insetInlineStart = "auto";
    this.panel.style.insetInlineEnd = "auto";
    // In a dialog, an ancestor (a transformed or contained card) can be
    // what `position: fixed` measures from instead of the viewport: a
    // zero-size probe placed at 0,0 there says how far that origin is.
    const origin = this.fixedOrigin();
    this.panel.style.top = `${top - origin.top}px`;
    this.panel.style.left = `${left - origin.left}px`;
  }

  fixedOrigin() {
    const host = this.panel.parentElement;
    if (!host || host === document.body) return { top: 0, left: 0 };
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;left:0;width:0;height:0;margin:0;translate:none;transform:none;visibility:hidden;pointer-events:none";
    host.appendChild(probe);
    const rect = probe.getBoundingClientRect();
    probe.remove();
    return { top: rect.top, left: rect.left };
  }

  closePopover(focusOnTrigger = false) {
    if (!this.isOpen()) return;
    this.trigger.setAttribute("aria-expanded", "false");
    this.panel.setAttribute("aria-hidden", "true");
    if (focusOnTrigger) this.trigger.focus();
    // Lets a caller distinguish "closed with a value confirmed" from
    // "dismissed/cleared with nothing set" -- e.g. a status button-group
    // pairing this picker with an option (see forms.html) can revert to
    // whatever was previously selected when the picker closes empty,
    // regardless of *how* it closed (Escape, outside click, re-toggling
    // the trigger, or Clear followed by any of the above).
    this.trigger.dispatchEvent(
      new CustomEvent("datetimepicker:closed", {
        bubbles: true,
        detail: { value: this.hiddenInput.value },
      }),
    );
  }

  togglePopover() {
    if (this.isOpen()) {
      this.closePopover();
    } else {
      this.openPopover();
    }
  }

  // ---- Keyboard navigation on the day grid ---------------------------

  moveFocus(fromDate, deltaDays, deltaMonths = 0) {
    let target = new Date(fromDate);
    if (deltaMonths) {
      target = new Date(target.getFullYear(), target.getMonth() + deltaMonths, target.getDate());
    } else {
      target = new Date(target.getFullYear(), target.getMonth(), target.getDate() + deltaDays);
    }

    if (target.getMonth() !== this.viewDate.getMonth() || target.getFullYear() !== this.viewDate.getFullYear()) {
      this.viewDate = new Date(target.getFullYear(), target.getMonth(), 1);
      this.renderMonthYearSelects();
      this.renderCalendar();
    }

    const cell = this.daysEl.querySelector(`[data-date="${this.dateKey(target)}"]`);
    if (cell) {
      this.daysEl.querySelectorAll(".widget-datetimepicker__day").forEach((el) => {
        el.tabIndex = -1;
      });
      cell.tabIndex = 0;
      cell.focus();
    }
  }

  // ---- Events ----------------------------------------------------------

  bindEvents() {
    // No trigger at all in inline mode (see the `inline` widget option) --
    // there's nothing to open/close, so none of this popover-lifecycle
    // wiring applies; isOpen()/openPopover()/closePopover() all assume
    // `this.trigger`/`this.panel` exist and are simply never called.
    if (this.trigger) {
      this.trigger.addEventListener("click", () => this.togglePopover());

      // Bound to `document`, not `this.container` -- the panel now lives in
      // <body> (see portalPanel()), outside `this.container`'s subtree, so a
      // listener scoped to the container would never see keydowns/clicks
      // that originate inside the calendar/time UI.
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.isOpen()) {
          this.closePopover(true);
        }
      });

      document.addEventListener("click", (event) => {
        if (!this.isOpen()) return;
        // `composedPath()` is the propagation path frozen at dispatch time --
        // unlike `event.target`, it stays correct even if picking a day (or
        // navigating months) re-renders the calendar grid and detaches the
        // originally-clicked node before this listener runs during the same
        // bubble phase. Using `.contains(event.target)` here previously
        // treated that as an "outside" click and closed the popover
        // mid-selection whenever a re-render happened to land first.
        const path = event.composedPath ? event.composedPath() : [event.target];
        if (
          path.includes(this.container) ||
          path.includes(this.trigger) ||
          path.includes(this.panel)
        ) {
          return;
        }
        this.closePopover(false);
      });

      // Fixed-positioned against the trigger's on-open coordinates -- if the
      // page scrolls while open, the panel would otherwise stay glued to the
      // same viewport spot instead of following the trigger, drifting
      // visibly out of alignment. Closing on scroll (capture: true catches
      // scroll events from any scrollable ancestor, since scroll doesn't
      // bubble) matches how most floating-UI popovers handle this.
      //
      // Guarded on the trigger having actually moved, rather than closing
      // on any scroll event at all. Browsers deliver `scroll`
      // asynchronously, on a later frame than the scrolling itself, so a
      // scroll that finished *before* the panel opened can still be
      // announced *after* it -- and an unconditional close then shut the
      // panel the instant it appeared. Clicking a trigger that is
      // off-screen reproduced that every time: the browser scrolls it
      // into view, the click opens the panel, and the queued scroll event
      // arrives a frame later and closes it again. On a phone, tapping
      // while a momentum scroll is still settling does the same.
      //
      // Comparing positions rather than debouncing on a timer, because it
      // encodes the actual reason for closing: the panel is pinned to
      // where the trigger was, so it only needs to close once the trigger
      // is somewhere else. A scroll that leaves it where it was has
      // nothing to correct for.
      window.addEventListener(
        "scroll",
        () => {
          if (!this.isOpen()) return;
          if (!this.anchorRectAtOpen) {
            this.closePopover(false);
            return;
          }
          const anchor = this.trigger.closest(".popover") || this.trigger;
          const rect = anchor.getBoundingClientRect();
          // A pixel of slack: sub-pixel layout jitter is not a scroll.
          const moved =
            Math.abs(rect.top - this.anchorRectAtOpen.top) > 1 ||
            Math.abs(rect.left - this.anchorRectAtOpen.left) > 1;
          if (moved) this.closePopover(false);
        },
        true,
      );

      document.addEventListener("basecoat:popover", (event) => {
        if (event.detail.source !== this.container) this.closePopover(false);
      });
      // dropdown-menu.js (this app's own, unrelated to the popover.js this
      // widget deliberately doesn't depend on) dispatches the same event
      // for its own dropdowns -- listening to it here means opening this
      // picker closes any open menu, and vice versa, matching how every
      // other dropdown/menu on the page already behaves.
    }

    if (this.monthSelect) {
      this.monthSelect.addEventListener("change", () => {
        this.viewDate = new Date(
          parseInt(this.yearSelect.value, 10),
          parseInt(this.monthSelect.value, 10),
          1,
        );
        this.renderCalendar();
      });
    }
    if (this.yearSelect) {
      this.yearSelect.addEventListener("change", () => {
        this.viewDate = new Date(
          parseInt(this.yearSelect.value, 10),
          parseInt(this.monthSelect.value, 10),
          1,
        );
        this.renderCalendar();
      });
    }

    const prevBtn = this.container.querySelector('[data-action="prev-month"]');
    const nextBtn = this.container.querySelector('[data-action="next-month"]');
    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
        this.renderMonthYearSelects();
        this.renderCalendar();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
        this.renderMonthYearSelects();
        this.renderCalendar();
      });
    }

    if (this.daysEl) {
      this.daysEl.addEventListener("click", (event) => {
        const button = event.target.closest(".widget-datetimepicker__day");
        if (!button || button.disabled) return;
        this.selectDay(this.parseDateOnly(button.dataset.date));
      });

      this.daysEl.addEventListener("keydown", (event) => {
        const button = event.target.closest(".widget-datetimepicker__day");
        if (!button) return;
        const current = this.parseDateOnly(button.dataset.date);

        const keyActions = {
          ArrowLeft: () => this.moveFocus(current, -1),
          ArrowRight: () => this.moveFocus(current, 1),
          ArrowUp: () => this.moveFocus(current, -7),
          ArrowDown: () => this.moveFocus(current, 7),
          PageUp: () => this.moveFocus(current, 0, event.shiftKey ? -12 : -1),
          PageDown: () => this.moveFocus(current, 0, event.shiftKey ? 12 : 1),
          Home: () => this.moveFocus(current, -((current.getDay() - this.firstDayOfWeek + 7) % 7)),
          End: () => this.moveFocus(current, 6 - ((current.getDay() - this.firstDayOfWeek + 7) % 7)),
        };

        if (keyActions[event.key]) {
          event.preventDefault();
          keyActions[event.key]();
        } else if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (!button.disabled) this.selectDay(current);
        }
      });
    }

    [this.hourInput, this.minuteInput, this.secondInput].forEach((input) => {
      if (!input) return;
      input.addEventListener("change", () => this.applyTimeFromInputs());
    });

    this.container.querySelectorAll("[data-spin-target]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const delta = parseInt(btn.dataset.spinDir, 10);
        if (btn.dataset.spinTarget === "hour") this.bumpHour(delta);
        else if (btn.dataset.spinTarget === "minute") this.bumpMinute(delta);
      });
    });

    if (this.ampmToggle) {
      this.ampmToggle.addEventListener("click", () => {
        const next = this.ampmToggle.dataset.value === "AM" ? "PM" : "AM";
        this.ampmToggle.dataset.value = next;
        this.ampmToggle.textContent = next;
        this.applyTimeFromInputs();
      });
    }

    const todayBtn = this.container.querySelector('[data-action="today"]');
    if (todayBtn) todayBtn.addEventListener("click", () => this.goToToday());

    const clearBtn = this.container.querySelector('[data-action="clear"]');
    if (clearBtn) clearBtn.addEventListener("click", () => this.clear());

    const doneBtn = this.container.querySelector('[data-action="done"]');
    if (doneBtn) {
      doneBtn.addEventListener("click", () => {
        if (this.hourInput) this.applyTimeFromInputs();
        this.ensureSelectedDate();
        this.commit();
        this.updateDisplay();
        this.closePopover(true);
      });
    }
  }
}

// Auto-initialize widgets when DOM is ready
// Mounts every widget in `root` not mounted yet: on load, and on
// `pystarter:widgets:init` (dispatched, bubbling, from a subtree that
// gained widgets after load -- a new InlineFormSet row; see forms.js and
// docs/widgets/protocol.md, "JavaScript").
function initDateTimePickerWidgets(root = document) {
  const selector = '[data-widget="datetimepicker"]';
  const widgets = [
    ...(root.matches?.(selector) ? [root] : []),
    ...(root.querySelectorAll ? root.querySelectorAll(selector) : []),
  ];
  widgets.forEach((container) => {
    if (!container.dateTimePickerWidget) {
      container.dateTimePickerWidget = new DateTimePickerWidget(container);
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initDateTimePickerWidgets());
} else {
  initDateTimePickerWidgets();
}

// Widgets added after load (a new formset row, a repeated field group
// added client-side).
document.addEventListener("pystarter:widgets:init", (event) => {
  initDateTimePickerWidgets(
    event.target instanceof Element ? event.target : document,
  );
});

export default DateTimePickerWidget;
